import styles from './primeiroAdquirenteNovaAquisicao.module.scss';
import { BrButton, BrDivider, BrRadioGroup, Col, Row } from '@govbr-ds/react-components';
import { IAquisicao } from '../../../../../interfaces/IAquisicao';
import { useContext, useEffect, useState } from 'react';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import { mesParaAbreviacao } from '../../../../../utils/mesParaAbreviacao';
import PrimeiroAdquirenteComponentesBaseDeCalculo from '../../primeiroAdquirenteComponentesBaseDeCalculo/primeiroAdquirenteComponentesBaseDeCalculo';

import {
  IComponenteBaseDeCalculoPrimeiroAdquirenteouArrematacao,
  ICodigoNcm as ICodigoNcmBaseCalculo,
} from '../../../../../interfaces/IBaseDeCalculo';
import { ICodigoNcm } from '../../../../../interfaces/ICodigoNcm';
import { formatarDataBRpISO, formatarDataISOpBR } from '../../../../../utils/formatDate';
import PrimeiroAdquirenteProcessosForm from './components/primeiroAdquirenteAquisicaoForm/primeiroAdquirenteAquisicaoForm';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { IMunicipio } from '../../../../../interfaces/IProcessos';
import { useNotificacao } from '../../../../../hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { formatarNrProcesso } from 'utils/formatNrProcesso';

interface IPrimeiroAdquirentNovaAquisicaoProps {
  setNovaAquisicao: React.Dispatch<React.SetStateAction<boolean>>;
  edicaoAquisicao: IAquisicao | undefined;
  setEdicaoAquisicao: React.Dispatch<React.SetStateAction<IAquisicao | undefined>>;
  salvarRascunho: () => void;
}

function PrimeiroAdquirenteNovaAquisicao({
  setNovaAquisicao,
  edicaoAquisicao,
  setEdicaoAquisicao,
  salvarRascunho,
}: Readonly<IPrimeiroAdquirentNovaAquisicaoProps>) {
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [substancia, setSubstancia] = useState('');
  const [municipio, setMunicipio] = useState<IMunicipio>();
  const [unidadeMedida, setUnidadeMedida] = useState('');
  const [estabelecimentoAdquirente, setEstabelecimentoAdquirente] = useState<string>('');
  const [isRejeito, setIsRejeito] = useState(false);
  const [hasIrregularidade, setHasIrregularidade] = useState(false);
  const [irregularidade, setIrregularidade] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const [isDadosMinerariosSalvos, setIsDadosMinerariosSalvos] = useState(!!edicaoAquisicao);

  const [listaComponentesBaseCalculo, setListaComponentesBaseCalculo] = useState<
    IComponenteBaseDeCalculoPrimeiroAdquirenteouArrematacao[]
  >([]);

  const [listaCodigosNCM, setListaCodigosNCM] = useState<ICodigoNcmBaseCalculo[]>([]);

  const [componenteBaseCalculoOption, setComponenteBaseCalculoOption] = useState('consolidado');

  const [isWithError, setIsWithError] = useState(false);

  const [codigosNCM, setCodigosNCM] = useState<ICodigoNcm[]>([]);

  const context = useContext(DiefCfemContext);
  const declaracaoPrimeiroAdquirente = context?.declaracaoPrimeiroAdquirente;
  const aquisicoes: IAquisicao[] = declaracaoPrimeiroAdquirente?.aquisicoes || [];

  const mostrarNotificacao = useNotificacao();

  const { getCodigosNCM } = useBackEndRequests();

  const { data: codigosNCMData } = useQuery<ICodigoNcm[], IErrorWithSentryId>({
    queryKey: ['codigosNCMQuery'],
    queryFn: () => getCodigosNCM(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (codigosNCMData) {
      setCodigosNCM(codigosNCMData);
    }
  }, [codigosNCMData]);

  useEffect(() => {
    if (edicaoAquisicao) {
      setNumeroProcesso(edicaoAquisicao.numeroProcesso);
      setSubstancia(edicaoAquisicao.substancia);
      setMunicipio(edicaoAquisicao.municipio);
      setUnidadeMedida(edicaoAquisicao.unidadeMedida);
      setEstabelecimentoAdquirente(edicaoAquisicao.estabelecimentoAdquirente || '');
      setIsRejeito(edicaoAquisicao.isRejeito);
      setHasIrregularidade(edicaoAquisicao.hasSuspeitaIrregularidade);
      setIrregularidade(edicaoAquisicao.descricaoSuspeitaIrregularidade || '');
      setObservacoes(edicaoAquisicao.observacoes || '');
      setListaComponentesBaseCalculo(
        edicaoAquisicao.componentesBaseDeCalculo.map((componente) => ({
          ...componente,
          dataEmissaoNF: formatarDataISOpBR(componente.dataEmissaoNF || ''),
          valorTotal: componente.codigosNCM
            .reduce((acc, item) => acc + parseFloat(item.valor), 0)
            .toFixed(2)
            .toString(),
          quantidadeTotal: componente.codigosNCM
            .reduce((acumulador, item) => acumulador + parseFloat(item.quantidade), 0)
            .toString(),
          notaFiscal: componente.notaFiscal || '',
          codigosNCM: componente.codigosNCM.map((codigo) => ({
            ...codigo,
            valor: codigo.valor,
            quantidade: codigo.quantidade.toString(),
            codigoNCM: codigo.codigoNCM,
          })),
        }))
      );
      edicaoAquisicao.isConsolidado
        ? setListaCodigosNCM(edicaoAquisicao.componentesBaseDeCalculo[0]?.codigosNCM || [])
        : setListaCodigosNCM([]);
      setComponenteBaseCalculoOption(edicaoAquisicao.isConsolidado ? 'consolidado' : 'documentoFiscal');
    }
  }, [edicaoAquisicao]);

  const salvarDadosProcesso = () => {
    if (numeroProcesso === '' || isWithError || numeroProcesso.replace(/\D/g, '').length !== 10) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Preencha o processo corretamente',
      });
    } else if (municipio === undefined || municipio.codigoIBGE === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione um município válido',
      });
    } else if (substancia === undefined || substancia === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione uma substância válida',
      });
    } else if (unidadeMedida === undefined || unidadeMedida === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione uma unidade de medida válida',
      });
    } else if (estabelecimentoAdquirente === undefined || estabelecimentoAdquirente === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione um estabelecimento adquirente válido',
      });
    } else if (
      !!declaracaoPrimeiroAdquirente?.aquisicoes?.filter(
        (obj) =>
          obj.municipio.codigoIBGE.toString() === municipio?.codigoIBGE.toString() &&
          obj.numeroProcesso.toString() === numeroProcesso.replace(/\D/g, '').toString() &&
          obj.substancia.toString() === substancia.toString()
      ).length &&
      edicaoAquisicao === undefined
    ) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Já existe uma aquisição com este número de processo, substância e município.',
      });
    } else if (hasIrregularidade && irregularidade.trim().length === 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Descreva a irregularidade encontrada',
      });
    } else {
      setIsDadosMinerariosSalvos(true);
    }
  };

  const concluirAdicao = () => {
    if (listaCodigosNCM.length === 0 && componenteBaseCalculoOption === 'consolidado') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Adicione pelo menos um item NCM',
      });
    } else if (listaComponentesBaseCalculo.length === 0 && componenteBaseCalculoOption === 'documentoFiscal') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Adicione pelo menos um componente de base de cálculo',
      });
    } else {
      if (componenteBaseCalculoOption === 'consolidado') {
        if (listaComponentesBaseCalculo.length === 0) {
          listaComponentesBaseCalculo.push({
            codigosNCM: listaCodigosNCM.map((codigo) => ({
              ...codigo,
              valor: codigo.valor,
              quantidade: codigo.quantidade,
              codigoNCM: codigo.codigoNCM,
            })),
            quantidadeTotal: listaCodigosNCM
              .reduce((acumulador, item) => {
                return acumulador + parseFloat(item.quantidade);
              }, 0)
              .toString(),
            valorTotal: listaCodigosNCM
              .reduce((acumulador, item) => {
                return acumulador + parseFloat(item.valor);
              }, 0)
              .toFixed(2)
              .toString(),
          });
        } else {
          listaComponentesBaseCalculo[0].codigosNCM = listaCodigosNCM;
          listaComponentesBaseCalculo[0].quantidadeTotal = listaCodigosNCM
            .reduce((acumulador, item) => {
              return acumulador + parseFloat(item.quantidade);
            }, 0)
            .toString();
          listaComponentesBaseCalculo[0].valorTotal = listaCodigosNCM
            .reduce((acumulador, item) => {
              return acumulador + parseFloat(item.valor);
            }, 0)
            .toFixed(2)
            .toString();
        }
      }
      const novaAquisicao = {
        numeroProcesso: numeroProcesso.replace(/\D/g, ''),
        substancia: substancia,
        municipio: {
          nome: municipio?.nome || '',
          uf: municipio?.uf || '',
          codigoIBGE: municipio?.codigoIBGE || '',
        },
        unidadeMedida: unidadeMedida,
        estabelecimentoAdquirente: estabelecimentoAdquirente,
        quantidadeTotal: parseFloat(
          listaComponentesBaseCalculo
            .reduce((acumulador, item) => acumulador + parseFloat(item.quantidadeTotal), 0)
            .toString()
        ),
        valorTotal: listaComponentesBaseCalculo
          .reduce((acc, item) => acc + parseFloat(item.valorTotal), 0)
          .toFixed(2)
          .toString(),
        componentesBaseDeCalculo: listaComponentesBaseCalculo.map((componente) => ({
          ...componente,
          notaFiscal: componente.notaFiscal?.trim() || '',
          dataEmissaoNF: formatarDataBRpISO(componente.dataEmissaoNF || ''),
          codigosNCM: componente.codigosNCM,
          quantidadeTotal: componente.codigosNCM
            .reduce((acumulador, item) => {
              return acumulador + parseFloat(item.quantidade);
            }, 0)
            .toString(),
          valorTotal: componente.codigosNCM
            .reduce((acumulador, item) => {
              return acumulador + parseFloat(item.quantidade);
            }, 0)
            .toFixed(2)
            .toString(),
        })),
        isConsolidado: componenteBaseCalculoOption === 'consolidado',
        isRejeito: isRejeito,
        hasSuspeitaIrregularidade: hasIrregularidade,
        descricaoSuspeitaIrregularidade: irregularidade.trim(),
        observacoes: observacoes.trim(),
      };
      if (edicaoAquisicao) {
        const index = aquisicoes.findIndex((aquisicao) => aquisicao === edicaoAquisicao);
        aquisicoes[index] = novaAquisicao;
      } else {
        aquisicoes.push(novaAquisicao);
      }

      if (declaracaoPrimeiroAdquirente) {
        declaracaoPrimeiroAdquirente.aquisicoes = aquisicoes;
      }
      window.scrollTo(0, 0);
      setEdicaoAquisicao(undefined);
      setNovaAquisicao(false);
      setTimeout(() => salvarRascunho(), 300);
    }
  };

  return (
    <>
      <div className={styles.containerPrincipal}>
        <div className={styles.titleContainer}>
          <h4 className={styles.title}>{edicaoAquisicao ? 'Editar Aquisição' : 'Nova Aquisição'}</h4>
          <span className={styles.mesAno}>
            {mesParaAbreviacao(
              parseInt(
                sessionStorage.getItem('mesDeclaracao') ?? declaracaoPrimeiroAdquirente?.exercicioMes.toString() ?? ''
              )
            )}{' '}
            {sessionStorage.getItem('anoDeclaracao')}
          </span>
        </div>
        <div className={styles.formProcessoContainer}>
          <div className={styles.subTitleContainer}>
            <p className={styles.subTitle}>Dados do Processo Minerário</p>
          </div>
          <PrimeiroAdquirenteProcessosForm
            numeroProcesso={numeroProcesso}
            setNumeroProcesso={setNumeroProcesso}
            substancia={substancia}
            setSubstancia={setSubstancia}
            municipio={municipio || { nome: '', uf: '', codigoIBGE: '' }}
            setMunicipio={setMunicipio}
            unidadeMedida={unidadeMedida}
            setUnidadeMedida={setUnidadeMedida}
            estabelecimentoAdquirente={estabelecimentoAdquirente}
            setEstabelecimentoAdquirente={setEstabelecimentoAdquirente}
            isRejeito={isRejeito}
            setIsRejeito={setIsRejeito}
            setIsWithError={setIsWithError}
            edicaoAquisicao={edicaoAquisicao}
            hasIrregularidade={hasIrregularidade}
            setHasIrregularidade={setHasIrregularidade}
            irregularidade={irregularidade}
            setIrregularidade={setIrregularidade}
            isDadosMinerariosSalvos={isDadosMinerariosSalvos}
            observacoes={observacoes}
            setObservacoes={setObservacoes}
          />
          {isDadosMinerariosSalvos && (
            <>
              <div className={styles.dividerContainer}>
                <BrDivider />
              </div>
              <div className={styles.subTitleContainer}>
                <span className={styles.subTitle}>
                  <br />
                  Componentes de Base de Cálculo da CFEM
                </span>
              </div>
              <div className={styles.optionsContainer}>
                <Row className={styles.row}>
                  <Col className={styles.col}>
                    <span className={styles.label}>Selecione a forma de apresentação da declaração</span>
                  </Col>
                  <Col>
                    <BrRadioGroup
                      inline
                      className={styles.col}
                      name='radiogroup'
                      onChange={(e) => {
                        setComponenteBaseCalculoOption(e.target.value);
                        setListaComponentesBaseCalculo([]);
                      }}
                      options={[
                        {
                          id: '0',
                          label: 'Consolidado',
                          value: 'consolidado',
                        },
                        {
                          id: '1',
                          label: 'Por Documento Fiscal (detalhada)',
                          value: 'documentoFiscal',
                        },
                      ]}
                      value={componenteBaseCalculoOption}
                    />
                  </Col>
                </Row>
              </div>

              <div className={styles.componentesBaseCalculoContainer}>
                <div className={styles.componentesTitle}>
                  {componenteBaseCalculoOption === 'consolidado'
                    ? 'Componente de Base de Cálculo Consolidado'
                    : 'Adicione um Componente de Base de Cálculo por Documento Fiscal'}
                </div>

                <PrimeiroAdquirenteComponentesBaseDeCalculo
                  option={componenteBaseCalculoOption}
                  listaComponentesBaseCalculo={listaComponentesBaseCalculo}
                  setListaComponentesBaseCalculo={setListaComponentesBaseCalculo}
                  listaCodigosNCM={listaCodigosNCM}
                  setListaCodigosNCM={setListaCodigosNCM}
                  codigosNCM={codigosNCM}
                  numeroProcesso={numeroProcesso}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.botaoContainer}>
        <BrButton
          secondary
          onClick={() => {
            window.scrollTo(0, 0);
            setEdicaoAquisicao(undefined);
            setNovaAquisicao(false);
          }}
        >
          Cancelar <i className={`fas fa-times fa-md`} />
        </BrButton>
        <BrButton
          data-testid='salvar-aquisicao'
          primary
          onClick={() => {
            isDadosMinerariosSalvos ? concluirAdicao() : salvarDadosProcesso();
          }}
        >
          Salvar <i className={`fas fa-check fa-md`} />
        </BrButton>
      </div>
    </>
  );
}

export default PrimeiroAdquirenteNovaAquisicao;
